// utils/dateUtil.js

/**
 * 格式化完整的日期时间
 * @param {Date|string|number} dateTime 需要格式化的日期、日期时间字符串或时间戳
 * @returns {string} 返回格式化的日期时间字符串，如果输入无效则返回空字符串
 */
export function formatDateTime(dateTime) {
    if (!dateTime) return '';

    let date;
    if (dateTime instanceof Date) {
        date = dateTime;
    } else if (typeof dateTime === 'number') {
        date = new Date(dateTime);
    } else if (typeof dateTime === 'string') {
        // 尝试解析不同格式的日期字符串
        date = new Date(dateTime);
        if (isNaN(date.getTime())) {
            // 如果标准解析失败，尝试手动解析
            const parts = dateTime.split(/[- :T]/);
            date = new Date(parts[0], parts[1] - 1, parts[2], parts[3] || 0, parts[4] || 0, parts[5] || 0);
        }
    } else {
        console.error('Invalid date input:', dateTime);
        return '';
    }

    if (isNaN(date.getTime())) {
        console.error('Failed to parse date:', dateTime);
        return '';
    }

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * 仅格式化日期
 * @param {Date|string|number} dateTime 需要格式化的日期、日期时间字符串或时间戳
 * @returns {string} 返回格式化的日期字符串，如果输入无效则返回空字符串
 */
export function formatDate(dateTime) {
    if (!dateTime) return '';

    let date;
    if (dateTime instanceof Date) {
        date = dateTime;
    } else if (typeof dateTime === 'number') {
        date = new Date(dateTime);
    } else if (typeof dateTime === 'string') {
        // 尝试解析不同格式的日期字符串
        date = new Date(dateTime);
        if (isNaN(date.getTime())) {
            // 如果标准解析失败，尝试手动解析
            const parts = dateTime.split(/[- :T]/);
            date = new Date(parts[0], parts[1] - 1, parts[2]);
        }
    } else {
        console.error('Invalid date input:', dateTime);
        return '';
    }

    if (isNaN(date.getTime())) {
        console.error('Failed to parse date:', dateTime);
        return '';
    }

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');

    return `${year}-${month}-${day}`;
}

/**
 * 格式化月份
 * @param {Date|string|number} dateTime 需要格式化的日期、日期时间字符串或时间戳
 * @returns {string} 返回格式化的年月字符串，如果输入无效则返回空字符串
 */
export function formatMonth(dateTime) {
    if (!dateTime) return '';

    let date;
    if (dateTime instanceof Date) {
        date = dateTime;
    } else if (typeof dateTime === 'number') {
        date = new Date(dateTime);
    } else if (typeof dateTime === 'string') {
        // 尝试解析不同格式的日期字符串
        date = new Date(dateTime);
        if (isNaN(date.getTime())) {
            // 如果标准解析失败，尝试手动解析
            const parts = dateTime.split(/[- :T]/);
            date = new Date(parts[0], parts[1] - 1, 1);
        }
    } else {
        console.error('Invalid date input:', dateTime);
        return '';
    }

    if (isNaN(date.getTime())) {
        console.error('Failed to parse date:', dateTime);
        return '';
    }

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');

    return `${year}-${month}`;
}

/**
 * 按中国时间（东八区 UTC+8）取日历日期，结果与运行环境所在时区无关。
 *
 * 与 formatDate 的区别：
 *  - formatDate 用 getFullYear()/getMonth()/getDate()，取的是「浏览器本地时区」的日期；
 *  - 本函数固定按 UTC+8 取日期，并支持 Excel 日期序列号（xlsx 未开 cellDates 时，日期单元格即为数字）。
 *
 * @param {Date|string|number} input Date 对象 / 日期字符串 / Excel 序列号 / 毫秒时间戳
 * @returns {string} 'YYYY-MM-DD'，无法解析时返回空字符串
 */
export function formatChinaDate(input) {
    if (input === null || input === undefined || input === '') return '';

    let ms;
    if (typeof input === 'number') {
        // Excel 日期序列号以 1899-12-30 为基准（序列号 25569 对应 1970-01-01）
        ms = (input > 0 && input < 3000000) ? (input - 25569) * 86400 * 1000 : input;
    } else if (input instanceof Date) {
        ms = input.getTime();
    } else if (typeof input === 'string') {
        // 先剥离出年月日再按 +08:00 计算：
        // '2026/4/28' 这类非 ISO 格式会被 new Date() 按本地时区解释，东八区下再取 UTC 日期即少一天。
        const m = input.trim().match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
        ms = m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) - 8 * 3600 * 1000
               : new Date(input).getTime();
    } else {
        return '';
    }

    if (isNaN(ms)) return '';
    // 先偏移到东八区，再取 UTC 日期，即得到中国的日历日期
    return new Date(ms + 8 * 3600 * 1000).toISOString().split('T')[0];
}
